import { populationsApi } from '@/lib/api'

export interface MosaicPickerLookup {
  personaId: string
  name: string
}

/** Mirrors the backend D3 cap (personaIds accepts at most 240 values per request). */
const PERSONA_IDS_REQUEST_CAP = 240

function normalizeNameKey(name: string): string {
  return name.trim().toLowerCase()
}

function isSearchableName(name: string): boolean {
  const trimmed = name.trim()
  return trimmed.length > 0 && normalizeNameKey(trimmed) !== 'respondent'
}

function dedupeById(lookups: Array<MosaicPickerLookup>): Array<MosaicPickerLookup> {
  const unique: Array<MosaicPickerLookup> = []
  const seen = new Set<string>()
  for (const lookup of lookups) {
    const personaId = lookup.personaId.trim()
    if (!personaId || seen.has(personaId)) {
      continue
    }
    seen.add(personaId)
    unique.push({ personaId, name: lookup.name.trim() })
  }
  return unique
}

function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const withResponse = error as { response?: { status?: unknown }; status?: unknown }
  const nestedStatus = withResponse.response?.status
  if (typeof nestedStatus === 'number') {
    return nestedStatus
  }
  if (typeof withResponse.status === 'number') {
    return withResponse.status
  }
  return undefined
}

/**
 * Fallback path: per-lookup name search with limit 1, matched by personaId when present,
 * else by normalized display name. Tiles without a real name stay on the initials fallback.
 * Only used when the batched personaIds request is unusable (old server, or transport error).
 * A 400/validation rejection is not a miss — do not fan out to this path.
 */
async function resolveByNameSearch(
  populationId: string,
  lookups: Array<MosaicPickerLookup>,
): Promise<Record<string, string>> {
  const imageByPersonaId: Record<string, string> = {}
  const searchable = lookups.filter((lookup) => isSearchableName(lookup.name))

  await Promise.all(
    searchable.map(async (lookup) => {
      try {
        const result = await populationsApi.getPersonasForPicker(populationId, {
          search: lookup.name,
          limit: 1,
        })
        const personas = Array.isArray(result?.personas) ? result.personas : []
        const byId = personas.find(
          (persona: { personaId?: string }) => persona.personaId === lookup.personaId,
        )
        const nameKey = normalizeNameKey(lookup.name)
        const byName = personas.find(
          (persona: { personaName?: string }) =>
            normalizeNameKey(persona.personaName || '') === nameKey,
        )
        const match = byId ?? byName
        const url = typeof match?.imageUrl === 'string' ? match.imageUrl.trim() : ''
        if (url) {
          imageByPersonaId[lookup.personaId] = url
        }
      } catch {
        // Initials fallback remains; a single picker miss must not fail the strip.
      }
    }),
  )

  return imageByPersonaId
}

/**
 * Fill missing mosaic portraits via the slim `picker` view only.
 * Never calls `getPersonas` (that payload ships proprietary narratives).
 *
 * Sends ONE batched request keyed by personaId (D3 image-only picker projection) instead of
 * N per-name searches. The id path bypasses the isSearchableName/'Respondent' skip — a tile
 * doesn't need a real name to be looked up by id, only to be searched by name. Falls back to
 * the per-name search path only when the response contains none of the requested ids (old
 * server that doesn't understand `personaIds`) or on a transport error. A 400 from UUID/cap
 * validation returns an empty map — it must not fan out to N-parallel name search. A partial
 * response (some ids missing an image) is a normal outcome, not a fallback trigger.
 */
export async function resolveMosaicPickerImages(
  populationId: string,
  lookups: Array<MosaicPickerLookup>,
): Promise<Record<string, string>> {
  const unique = dedupeById(lookups)

  if (!populationId || unique.length === 0) {
    return {}
  }

  const personaIds = unique.map((lookup) => lookup.personaId).slice(0, PERSONA_IDS_REQUEST_CAP)

  try {
    const result = await populationsApi.getPersonasForPicker(populationId, {
      personaIds,
      limit: personaIds.length,
    })
    const personas = Array.isArray(result?.personas) ? result.personas : []
    const returnedIds = new Set(
      personas
        .map((persona: { personaId?: string }) => persona.personaId)
        .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0),
    )
    const matchedAny = personaIds.some((id) => returnedIds.has(id))

    if (!matchedAny) {
      return await resolveByNameSearch(populationId, unique)
    }

    const imageByPersonaId: Record<string, string> = {}
    for (const persona of personas as Array<{ personaId?: string; imageUrl?: string | null }>) {
      const personaId = typeof persona.personaId === 'string' ? persona.personaId : ''
      const url = typeof persona.imageUrl === 'string' ? persona.imageUrl.trim() : ''
      if (personaId && url) {
        imageByPersonaId[personaId] = url
      }
    }
    return imageByPersonaId
  } catch (error) {
    // UUID/cap validation (400) is a client/request rejection, not an old-server miss.
    if (getHttpStatus(error) === 400) {
      return {}
    }
    // Transport error on the batched request — fall back to per-name search
    // rather than leaving every tile on the initials fallback.
    return await resolveByNameSearch(populationId, unique)
  }
}
